import React, { useCallback, useEffect, useState } from "react";
import './alertBox.style.scss';
import { runAlertPlayersForSentAt, runRecentAlertSummary } from "../../../utils/loadFunctions.ts";
import { usePlayer } from "../../../Contexts/playerData.context.jsx";
import { useNavBar } from "../../../Contexts/navbar.items.context.jsx";
import { useDataLayer } from "../../../Contexts/DataLayer.context.jsx";
import DragBox2026 from "../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx";

const TRANSACTION_ALERT_LABEL = "Transaction";

const AlertBox = () => {
    const { setPlayerData, setListLoadID, setPlayerID, setActivePlayer } = usePlayer();
    const { updateActiveSideButton } = useNavBar();
    const { updateWhichOneLiner } = useDataLayer();
    const [showInfo, updateShowInfo] = useState(false);
    const [deadSearch, updateDeadSearch] = useState(false);
    const [alertRows, setAlertRows] = useState([]);
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [previewPlayers, setPreviewPlayers] = useState([]);
    const [loadingAlerts, setLoadingAlerts] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [visibleAlertCount, setVisibleAlertCount] = useState(5);

    const DEFAULT_POS = { x: 57, y: 295 };
    const STORAGE_KEY_POS = "Alert_BOX_LOCATION";

    const formatAlertDateTime = (dateValue) => {
        if (!dateValue) return "";
        const [datePart, timePart = ""] = dateValue.split(" ");
        const [year, month, day] = datePart.split("-");
        if (!year || !month || !day) return dateValue;
        const shortTime = timePart ? ` ${timePart.slice(0, 5)}` : "";
        return `${month}/${day}/${year}${shortTime}`;
    };

    const getTypeSummary = (row) => {
        const parts = [];
        if (Number(row.transactionCount) > 0) parts.push(`${row.transactionCount} Transaction${Number(row.transactionCount) === 1 ? "" : "s"}`);
        if (Number(row.noteCount) > 0) parts.push(`${row.noteCount} Note${Number(row.noteCount) === 1 ? "" : "s"}`);
        if (Number(row.reportCount) > 0) parts.push(`${row.reportCount} Report${Number(row.reportCount) === 1 ? "" : "s"}`);
        return parts.join(" | ");
    };

    const formatAlertReason = (reason) => {
        const text = String(reason || "Trans").trim();
        return text
            .split(",")
            .map((part) => part.trim() === "Trans" ? TRANSACTION_ALERT_LABEL : part.trim())
            .filter(Boolean)
            .join(", ");
    };

    const clearPreview = useCallback(() => {
        setSelectedAlert(null);
        setPreviewPlayers([]);
    }, []);

    const loadRecentAlerts = useCallback(async () => {
        updateDeadSearch(false);
        clearPreview();
        setVisibleAlertCount(5);
        setLoadingAlerts(true);
        try {
            const theData = await runRecentAlertSummary();
            const rows = Array.isArray(theData?.alerts) ? theData.alerts : [];
            setAlertRows(rows);
            updateDeadSearch(rows.length === 0);
        } catch (error) {
            console.error("Error occurred while loading recent alerts:", error);
            setAlertRows([]);
            updateDeadSearch(true);
        } finally {
            setLoadingAlerts(false);
        }
    }, [clearPreview]);

    useEffect(() => {
        loadRecentAlerts();
    }, [loadRecentAlerts]);

    const previewAlertPlayers = async (alertRow) => {
        if (selectedAlert?.alertSentAt === alertRow.alertSentAt) {
            clearPreview();
            return;
        }
        setSelectedAlert(alertRow);
        setPreviewPlayers([]);
        setLoadingPreview(true);
        try {
            const theData = await runAlertPlayersForSentAt(alertRow.alertSentAt);
            setPreviewPlayers(Array.isArray(theData?.returnArray) ? theData.returnArray : []);
        } catch (error) {
            console.error("Error occurred while loading alert players:", error);
            setPreviewPlayers([]);
        } finally {
            setLoadingPreview(false);
        }
    };

    const loadPreviewPlayers = () => {
        if (previewPlayers.length === 0) return;
        updateDeadSearch(false);
        updateWhichOneLiner(null);
        setPlayerData(previewPlayers);
        updateActiveSideButton(null);
        setPlayerID(previewPlayers[0].FBXID);
        setActivePlayer(previewPlayers[0]);
        const randomnum = Math.floor(Math.random() * 1_000_000) + 1;
        setListLoadID(randomnum);
    };

    const openMore = () => {
        updateShowInfo(!showInfo);
    }

    const showMoreAlerts = () => {
        setVisibleAlertCount(10);
    };

    const showFewerAlerts = () => {
        setVisibleAlertCount(5);
        if (selectedAlert && !alertRows.slice(0, 5).some((row) => row.alertSentAt === selectedAlert.alertSentAt)) {
            clearPreview();
        }
    };

    const visibleAlertRows = alertRows.slice(0, visibleAlertCount);

    return (
        <DragBox2026
            storageKey={STORAGE_KEY_POS}
            defaultPos={DEFAULT_POS}
            minWidth={900}
            maxWidth={900}
            showReset={true}
            title="Alert Notification Display"
        >

            <div className="alert-box-shell">
                <div className="alert-box-header">
                    <div>
                        <div className="alert-box-title">Alert Notifications</div>
                        <div className="alert-box-subtitle">Your recent alerts are shown below. Click one to preview the players, then Load Players when you want that alert in the main list.</div>
                    </div>
                    <button
                        type="button"
                        onClick={() => openMore()}
                        className={`btn btn-sm px-3 ${showInfo ? 'btn-warning' : 'btn-info'}`}
                    >
                        ?
                    </button>
                </div>

                {showInfo &&
                    <div className="alert-box-info">
                        <div className="fw-bold mb-1">Recent Alerts</div>
                        <p>This starts with the last 5 alert messages sent to you across transactions, notes, and reports. Use Show More to see up to 10.</p>
                        <p>Opening an alert shows the affected players directly below it. Press Load Players to send them to the main player list.</p>
                    </div>
                }

                {deadSearch &&
                    <div className="alert alert-warning text-center">No Alerts Were Found</div>
                }
                <div className="alert-results-panel">
                    <div className="alert-results-heading">
                        <div>
                            <div className="fw-bold">Recent Alerts</div>
                            <div className="alert-results-subtitle">Last {Math.min(visibleAlertCount, alertRows.length || visibleAlertCount)} alerts received</div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-light btn-sm alert-refresh-button"
                            onClick={loadRecentAlerts}
                            disabled={loadingAlerts}
                        >
                            Refresh
                        </button>
                    </div>
                    {loadingAlerts && <div className="alert-empty-message">Loading alerts...</div>}
                    {!loadingAlerts && alertRows.length > 0 && (
                        <div className="alert-row-list recent-alert-row-list">
                            {visibleAlertRows.map((row) => {
                                const isOpen = selectedAlert?.alertSentAt === row.alertSentAt;
                                return (
                                    <div className={`alert-row-item ${isOpen ? "open" : ""}`} key={row.alertSentAt}>
                                        <button
                                            type="button"
                                            className={`alert-row-button ${isOpen ? "active" : ""}`}
                                            onClick={() => previewAlertPlayers(row)}
                                        >
                                            <span>
                                                <span className="alert-row-date">{formatAlertDateTime(row.alertSentAt)}</span>
                                                <span className="alert-row-types">{getTypeSummary(row)}</span>
                                            </span>
                                            <span className="alert-row-count">{row.playerCount} Player{Number(row.playerCount) === 1 ? "" : "s"}</span>
                                        </button>
                                        {isOpen && (
                                            <div className="alert-inline-preview">
                                                <div className="alert-inline-preview-heading">
                                                    <div>
                                                        <div className="fw-bold">Affected Players</div>
                                                        <div className="alert-results-subtitle">{row.playerCount} Player{Number(row.playerCount) === 1 ? "" : "s"}</div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-success btn-sm text-white px-4"
                                                        onClick={loadPreviewPlayers}
                                                        disabled={loadingPreview || previewPlayers.length === 0}
                                                    >
                                                        Load Players
                                                    </button>
                                                </div>
                                                {loadingPreview && <div className="alert-empty-message">Loading players...</div>}
                                                {!loadingPreview && previewPlayers.length > 0 && (
                                                    <div className="alert-preview-list alert-inline-player-list">
                                                        {previewPlayers.map((player) => (
                                                            <div className="alert-preview-player" key={player.FBXID}>
                                                                <span>{player.first} {player.last}</span>
                                                                <span>
                                                                    <strong>{formatAlertReason(player.alertReason)}</strong>
                                                                    {" | "}
                                                                    {player.colPos || player.proPos || "-"} | {player.school || "-"}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {!loadingPreview && previewPlayers.length === 0 && (
                                                    <div className="alert-empty-message">No players available for this alert.</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {alertRows.length > 5 && (
                                <div className="alert-show-more-wrap">
                                    {visibleAlertCount < 10 ? (
                                        <button
                                            type="button"
                                            className="btn btn-light btn-sm alert-show-more-button"
                                            onClick={showMoreAlerts}
                                        >
                                            Show More
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-light btn-sm alert-show-more-button"
                                            onClick={showFewerAlerts}
                                        >
                                            Show Less
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </DragBox2026>
    );
};

export default AlertBox;
